import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import { setPassword } from "@/app/api/legacy-api";
import { PasswordTips } from "@/app/components/legacy-components";
import { ROUTES } from "@/app/router";
import { Logo } from "@/assets";
import { confirmPasswordSchema } from "@/shared/models/legacy-schemas";
import {
  Button,
  CircleIcon,
  Form,
  Heading,
  PasswordInput,
  Text,
  VerifiedCheckIcon,
} from "@/shared/ui";
import { handleAxiosFieldErrors } from "@/shared/utils";

export const ResetPassword = () => {
  const token = new URLSearchParams(window.location.search).get("token");

  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(confirmPasswordSchema),
  });

  const {
    mutate: resetPasswordMutation,
    isPending,
    isSuccess,
  } = useMutation({
    mutationFn: setPassword.mutation,
    onError: (error) => {
      handleAxiosFieldErrors(error, form.setError);
    },
  });

  const handleSubmit = form.handleSubmit((values) => {
    resetPasswordMutation({ ...values, token });
  });

  return (
    <>
      <header className="flex w-full flex-col items-center gap-16">
        <Logo className="h-8 sm:h-11" />

        {isSuccess ? (
          <div className="flex w-full flex-col items-center gap-6">
            <CircleIcon size="lg" icon={VerifiedCheckIcon} />

            <div className="flex flex-col items-center gap-2">
              <Heading size="medium">{t`All done!`}</Heading>

              <Text align="center">
                {t`Your password has been reset successfully!`}
                <br />
                {t`Please return to the main screen to log in.`}
              </Text>
            </div>

            <Button
              size="lg"
              onClick={() => navigate(ROUTES.LOGIN)}
              className="w-full"
            >
              {t`Back to Log in`}
            </Button>
          </div>
        ) : (
          <Heading size="medium">{t`Reset password`}</Heading>
        )}
      </header>

      {!isSuccess && (
        <Form.Provider {...form}>
          <form onSubmit={handleSubmit} className="flex w-full flex-col gap-16">
            <div className="flex flex-col">
              <Form.Field
                control={form.control}
                name="password"
                render={() => (
                  <Form.Item>
                    <Form.Label aria-required>{t`Password`}</Form.Label>
                    <Form.Control>
                      <PasswordInput
                        {...form.register("password")}
                        placeholder={t`Your new password here`}
                      />
                    </Form.Control>
                    <Form.Message compact={false} />
                  </Form.Item>
                )}
              />

              <Form.Field
                control={form.control}
                name="passwordConfirmation"
                render={() => (
                  <Form.Item>
                    <Form.Label aria-required>{t`Confirm password`}</Form.Label>
                    <Form.Control>
                      <PasswordInput
                        {...form.register("passwordConfirmation")}
                        placeholder={t`Confirm your password here`}
                      />
                    </Form.Control>
                    <Form.Message compact={false} />
                  </Form.Item>
                )}
              />

              <PasswordTips />
            </div>

            <div className="flex flex-col gap-4">
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isPending}
              >
                {t`Save changes`}
              </Button>

              <Button
                size="lg"
                variant="secondary"
                onClick={() => navigate(ROUTES.LOGIN)}
                className="w-full"
              >
                {t`Cancel`}
              </Button>
            </div>
          </form>
        </Form.Provider>
      )}
    </>
  );
};
